import React from "react";
import AuthLayout from "./AuthLayout";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, IconButton, InputAdornment, TextField } from "@mui/material";
import { useChangePasswordMutation } from "../../service/auth/api";
import { toast } from "react-toastify";

import { VisibilityOff, Visibility } from "@mui/icons-material";
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = location.state || {}; // Retrieve token from navigation state

  const [passwords, setPasswords] = React.useState({
    new: "",
    confirm: "",
  });
  const [passwordVisibility, setPasswordVisibility] = React.useState({
    new: false,
    confirm: false,
  });
  const [errors, setErrors] = React.useState({
    new: "",
    confirm: "",
  });

  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const toggleVisibility = (field: "new" | "confirm") => {
    setPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    // Map id to state keys
    const key = id === "new-password" ? "new" : "confirm";
    setPasswords((prev) => ({ ...prev, [key]: value }));
    // Clear errors on change
    if (errors[key as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const validate = () => {
    let isValid = true;
    const newErrors = { new: "", confirm: "" };

    if (!passwords.new) {
      newErrors.new = "Password is required";
      isValid = false;
    } else {
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;
      if (!passwordRegex.test(passwords.new)) {
        newErrors.new = "Password must be at least 8 characters long, contain at least one uppercase letter, one lowercase letter, and one symbol";
        isValid = false;
      }
    }

    if (!passwords.confirm) {
      newErrors.confirm = "Confirm Password is required";
      isValid = false;
    } else if (passwords.new !== passwords.confirm) {
      newErrors.confirm = "Passwords do not match";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    if (!token) {
      toast.error("Session expired or invalid token. Please restart the process.");
      navigate("/forgot-password");
      return;
    }

    try {
      const payload = {
        data: {
          newPassword: passwords.new,
          isResetPassword: true
        },
        token: token
      };

      const result = await changePassword(payload).unwrap();

      toast.success(result.message || "Password changed successfully");
      navigate("/"); // Redirect to login
    } catch (err: any) {
      console.error("Change Password Error:", err);
      toast.error(err?.data?.message || "Failed to change password");
    }
  };

  return (
    <AuthLayout>
      <div className="authBox">
        <h2>Reset Password</h2>
        <p>Enter New Password</p>
        <form className="form" onSubmit={handleSubmit}>
          <div className="control_group">
            <label htmlFor="new-password">New Password</label>
            <TextField
              id="new-password"
              placeholder="Enter New Password"
              fullWidth
              hiddenLabel
              type={passwordVisibility.new ? "text" : "password"}
              value={passwords.new}
              onChange={handleChange}
              error={!!errors.new}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={
                        passwordVisibility.new ? "Hide password" : "Show password"
                      }
                      onClick={() => toggleVisibility("new")}
                      onMouseDown={handleMouseDown}
                      edge="end"
                    >
                      {passwordVisibility.new ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {errors.new && <p className="text_danger" style={{ marginTop: "5px", fontSize: "14px", color: "red" }}>{errors.new}</p>}
          </div>

          <div className="control_group">
            <label htmlFor="confirm-password">Confirm Password</label>
            <TextField
              id="confirm-password"
              placeholder="Enter Confirm Password"
              fullWidth
              hiddenLabel
              type={passwordVisibility.confirm ? "text" : "password"}
              value={passwords.confirm}
              onChange={handleChange}
              error={!!errors.confirm}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={
                        passwordVisibility.confirm ? "Hide password" : "Show password"
                      }
                      onClick={() => toggleVisibility("confirm")}
                      onMouseDown={handleMouseDown}
                      edge="end"
                    >
                      {passwordVisibility.confirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {errors.confirm && <p className="text_danger" style={{ marginTop: "5px", fontSize: "14px", color: "red" }}>{errors.confirm}</p>}
          </div>

          <div className="form_btn">
            <Button
              className="btn btn_primary w_100"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </form>

        <div className="auth_bottom">
          <p>
            <button className="link" onClick={() => navigate("/")}>
              <KeyboardBackspaceIcon /> Return to Login
            </button>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
